import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Badge, Spinner, Alert, Modal, Form } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import OfferModal from '../messages/OfferModal';

const UserProfileView = () => {
  const { username } = useParams();
  const navigate = useNavigate();

  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isFavorite, setIsFavorite] = useState(false);
  const [isBlocked, setIsBlocked] = useState(false);
  const [lookups, setLookups] = useState({});
  const [lookupsLoaded, setLookupsLoaded] = useState(false);

  // Photo gallery states
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState(0);

  // Modal states
  const [showReportModal, setShowReportModal] = useState(false);
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [showOfferModal, setShowOfferModal] = useState(false);
  const [reportReason, setReportReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [actionSuccess, setActionSuccess] = useState(null);

  // Fetch lookups data
  useEffect(() => {
    const fetchLookups = async () => {
      try {
        const response = await api.get('/lookups?tag=profile');
        const lookupData = {};

        if (response.data && response.data.data) {
          response.data.data.forEach(lookup => {
            lookupData[lookup.name] = lookup.choices;
          });
        }

        setLookups(lookupData);
        setLookupsLoaded(true);
      } catch (err) {
        console.error('Error fetching lookups:', err);
      }
    };

    fetchLookups();
  }, []);

  // Fetch profile data
  useEffect(() => {
    const fetchProfileData = async () => {
      try {
        setLoading(true);
        const response = await api.get(`/users/${username}/profile`);
        setProfileData(response.data);

        // Determine favorite and block status based on links in the response
        if (response.data?.user?._links) {
          // If unfavorite link exists, the user is already favorited
          const isUserFavorited = 'unfavorite' in response.data.user._links;
          setIsFavorite(isUserFavorited);

          // If unblock link exists, the user is already blocked
          const isUserBlocked = 'unblock' in response.data.user._links;
          setIsBlocked(isUserBlocked);
        }
      } catch (err) {
        console.error('Error fetching profile data:', err);
        setError('Failed to load profile data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchProfileData();
  }, [username]);

  // Helper function to get display value for a lookup field
  const getDisplayValue = (fieldName, value) => {
    if (!value || !lookups[fieldName]) return 'Not specified';

    const option = lookups[fieldName].find(opt => opt.id === parseInt(value));
    return option ? option.value : 'Not specified';
  };

  const handleMessage = () => {
    navigate(`/messages/${username}`);
  };

  const handleToggleFavorite = async () => {
    try {
      setSubmitting(true);
      if (isFavorite) {
        await api.post(`/users/${username}/unfavorite`);
        setActionSuccess('User removed from favorites');
      } else {
        await api.post(`/users/${username}/favorite`);
        setActionSuccess('User added to favorites');
      }

      // Update the favorite status
      setIsFavorite(!isFavorite);

      // Refresh the profile data to get updated links
      try {
        const response = await api.get(`/users/${username}/profile`);
        setProfileData(response.data);

        // Update favorite status based on links in the response
        if (response.data?.user?._links) {
          const isUserFavorited = 'unfavorite' in response.data.user._links;
          setIsFavorite(isUserFavorited);
        }
      } catch (err) {
        console.error('Error refreshing profile data:', err);
      }

      // Clear success message after 3 seconds
      setTimeout(() => {
        setActionSuccess(null);
      }, 3000);
    } catch (err) {
      console.error('Error toggling favorite:', err);
      setError('Failed to update favorite status. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReport = async () => {
    if (!reportReason.trim()) {
      return;
    }

    try {
      setSubmitting(true);

      // Create FormData directly
      const formData = new FormData();
      formData.append('reason', reportReason);

      // Send the report with form data
      await api.post(`/users/${username}/report`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setShowReportModal(false);
      setReportReason('');
      setActionSuccess('User has been reported');

      // Clear success message after 3 seconds
      setTimeout(() => {
        setActionSuccess(null);
      }, 3000);
    } catch (err) {
      console.error('Error reporting user:', err);
      setError('Failed to report user. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleBlock = async () => {
    try {
      setSubmitting(true);

      if (isBlocked) {
        // Unblock the user
        await api.post(`/users/${username}/unblock`);
        setActionSuccess('User has been unblocked');
      } else {
        // Block the user
        await api.post(`/users/${username}/block`);
        setActionSuccess('User has been blocked');
      }

      setShowBlockModal(false);

      // Update the block status
      setIsBlocked(!isBlocked);

      // Refresh the profile data to get updated links
      try {
        const response = await api.get(`/users/${username}/profile`);
        setProfileData(response.data);

        // Update block status based on links in the response
        if (response.data?.user?._links) {
          const isUserBlocked = 'unblock' in response.data.user._links;
          setIsBlocked(isUserBlocked);
        }
      } catch (err) {
        console.error('Error refreshing profile data:', err);
      }

      // Clear success message after 3 seconds
      setTimeout(() => {
        setActionSuccess(null);
      }, 3000);
    } catch (err) {
      console.error('Error toggling block status:', err);
      setError(`Failed to ${isBlocked ? 'unblock' : 'block'} user. Please try again.`);
    } finally {
      setSubmitting(false);
    }
  };

  // Photo gallery handlers
  const handleOpenPhotoModal = (index) => {
    setSelectedPhotoIndex(index);
    setShowPhotoModal(true);
  };

  const handleClosePhotoModal = () => {
    setShowPhotoModal(false);
  };

  const handlePrevPhoto = () => {
    if (profileData?.photos?.length > 0) {
      setSelectedPhotoIndex((prevIndex) =>
        prevIndex === 0 ? profileData.photos.length - 1 : prevIndex - 1
      );
    }
  };

  const handleNextPhoto = () => {
    if (profileData?.photos?.length > 0) {
      setSelectedPhotoIndex((prevIndex) =>
        prevIndex === profileData.photos.length - 1 ? 0 : prevIndex + 1
      );
    }
  };

  const handleOfferSuccess = (newOffer) => {
    setActionSuccess('Date offer sent successfully! Check your messages to continue the conversation.');

    // Clear success message after 3 seconds
    setTimeout(() => {
      setActionSuccess(null);
      // Navigate to messages with this user
      navigate(`/messages/${username}`);
    }, 3000);
  };

  // Keyboard navigation for photo gallery
  const handleKeyDown = (e) => {
    if (!showPhotoModal) return;

    switch (e.key) {
      case 'ArrowLeft':
        handlePrevPhoto();
        break;
      case 'ArrowRight':
        handleNextPhoto();
        break;
      case 'Escape':
        handleClosePhotoModal();
        break;
      default:
        break;
    }
  };

  // Add keyboard event listener when modal is open
  useEffect(() => {
    if (showPhotoModal) {
      window.addEventListener('keydown', handleKeyDown);
    }

    // Cleanup event listener
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [showPhotoModal, selectedPhotoIndex]);

  if (loading) {
    return (
      <Container className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3">Loading profile...</p>
      </Container>
    );
  }

  // Show loading indicator while lookups are being fetched
  if (!lookupsLoaded && !error) {
    return (
      <Container className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3">Loading profile data...</p>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="py-5">
        <Alert variant="danger">{error}</Alert>
      </Container>
    );
  }

  if (!profileData) {
    return (
      <Container className="py-5">
        <Alert variant="warning">Profile not found</Alert>
      </Container>
    );
  }

  const { user, profile, photos } = profileData;

  return (
    <Container className="py-4">
      {actionSuccess && (
        <Alert variant="success" className="mb-4">
          {actionSuccess}
        </Alert>
      )}

      <Row>
        <Col lg={4} className="mb-4">
          <Card className="border-0 shadow-sm">
            <Card.Body className="text-center p-4">
              <div className="mb-3">
                {user?.avatars?.medium ? (
                  <img
                    src={user.avatars.medium}
                    alt={user.username}
                    className="avatar-large mx-auto d-block rounded-circle"
                    style={{ width: '150px', height: '150px', objectFit: 'cover' }}
                  />
                ) : (
                  <div className="avatar-large mx-auto d-flex align-items-center justify-content-center bg-light rounded-circle"
                       style={{ width: '150px', height: '150px' }}>
                    <i className="bi bi-person-fill fs-1 text-secondary"></i>
                  </div>
                )}
              </div>

              <h3 className="fw-bold mb-1">{user?.username}</h3>
              <p className="text-muted mb-3">
                {user?.age} • {user?.city}, {user?.region}
              </p>

              {user?.online && (
                <Badge bg="success" className="mb-3">Online Now</Badge>
              )}

              <div className="d-grid gap-2">
                <Button onClick={handleMessage} className="btn-primary">
                  <i className="bi bi-chat-fill me-2"></i>
                  Message
                </Button>
                <Button
                  onClick={handleToggleFavorite}
                  className={isFavorite ? "btn-profile" : "btn-profile"}
                  disabled={submitting}
                >
                  <i className={`bi ${isFavorite ? 'bi-heart-fill' : 'bi-heart'} me-2`}></i>
                  {isFavorite ? 'Remove from Favorites' : 'Add to Favorites'}
                </Button>
              </div>
            </Card.Body>
          </Card>

          <Card className="border-0 shadow-sm mt-4">
            <Card.Body>
              <h5 className="fw-bold mb-3">Actions</h5>
              <div className="d-grid gap-2">
                <Button
                  variant="outline-warning"
                  onClick={() => setShowReportModal(true)}
                >
                  <i className="bi bi-flag-fill me-2"></i>
                  Report Profile
                </Button>
                <Button
                  variant={isBlocked ? "outline-success" : "outline-danger"}
                  onClick={() => setShowBlockModal(true)}
                >
                  <i className={`bi ${isBlocked ? 'bi-person-check-fill' : 'bi-slash-circle-fill'} me-2`}></i>
                  {isBlocked ? 'Unblock User' : 'Block User'}
                </Button>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={8}>
          <Card className="border-0 shadow-sm mb-4">
            <Card.Body className="p-4">
              <h4 className="fw-bold mb-4">Profile Details</h4>

              {/* Basic Information */}
              <h5 className="fw-bold mb-3">Basic Information</h5>
              <Row className="mb-4">
                {profile?.sex && (
                  <Col sm={6} className="mb-3">
                    <small className="text-muted d-block">Gender</small>
                    <span>{getDisplayValue('sex', profile.sex)}</span>
                  </Col>
                )}
                {profile?.orientation && (
                  <Col sm={6} className="mb-3">
                    <small className="text-muted d-block">Orientation</small>
                    <span>{getDisplayValue('orientation', profile.orientation)}</span>
                  </Col>
                )}
                {profile?.looking_to && (
                  <Col sm={6} className="mb-3">
                    <small className="text-muted d-block">Looking To</small>
                    <span>{getDisplayValue('looking_to', profile.looking_to)}</span>
                  </Col>
                )}
                {(user?.city || user?.region || user?.country) && (
                  <Col sm={6} className="mb-3">
                    <small className="text-muted d-block">Location</small>
                    <span>
                      {[
                        user?.city,
                        user?.region,
                        user?.country
                      ].filter(Boolean).join(', ') || 'Not specified'}
                    </span>
                  </Col>
                )}
              </Row>

              {/* Physical Attributes */}
              {(profile?.height || profile?.body_type || profile?.eyes || profile?.ethnicity) && (
                <>
                  <h5 className="fw-bold mb-3">Physical Attributes</h5>
                  <Row className="mb-4">
                    {profile?.height && (
                      <Col sm={6} className="mb-3">
                        <small className="text-muted d-block">Height</small>
                        <span>{getDisplayValue('height', profile.height)}</span>
                      </Col>
                    )}
                    {profile?.body_type && (
                      <Col sm={6} className="mb-3">
                        <small className="text-muted d-block">Body Type</small>
                        <span>{getDisplayValue('body_type', profile.body_type)}</span>
                      </Col>
                    )}
                    {profile?.eyes && (
                      <Col sm={6} className="mb-3">
                        <small className="text-muted d-block">Eyes</small>
                        <span>{getDisplayValue('eyes', profile.eyes)}</span>
                      </Col>
                    )}
                    {profile?.ethnicity && (
                      <Col sm={6} className="mb-3">
                        <small className="text-muted d-block">Ethnicity</small>
                        <span>{getDisplayValue('ethnicity', profile.ethnicity)}</span>
                      </Col>
                    )}
                  </Row>
                </>
              )}

              {/* Lifestyle */}
              {(profile?.relationship_status || profile?.children || profile?.smoker ||
                profile?.drinker || profile?.pot_friendly || profile?.party_drugs) && (
                <>
                  <h5 className="fw-bold mb-3">Lifestyle</h5>
                  <Row className="mb-4">
                    {profile?.relationship_status && (
                      <Col sm={6} className="mb-3">
                        <small className="text-muted d-block">Relationship Status</small>
                        <span>{getDisplayValue('relationship_status', profile.relationship_status)}</span>
                      </Col>
                    )}
                    {profile?.children && (
                      <Col sm={6} className="mb-3">
                        <small className="text-muted d-block">Children</small>
                        <span>{getDisplayValue('children', profile.children)}</span>
                      </Col>
                    )}
                    {profile?.smoker && (
                      <Col sm={6} className="mb-3">
                        <small className="text-muted d-block">Smoker</small>
                        <span>{getDisplayValue('smoker', profile.smoker)}</span>
                      </Col>
                    )}
                    {profile?.drinker && (
                      <Col sm={6} className="mb-3">
                        <small className="text-muted d-block">Drinker</small>
                        <span>{getDisplayValue('drinker', profile.drinker)}</span>
                      </Col>
                    )}
                    {profile?.pot_friendly && (
                      <Col sm={6} className="mb-3">
                        <small className="text-muted d-block">Pot Friendly</small>
                        <span>{getDisplayValue('pot_friendly', profile.pot_friendly)}</span>
                      </Col>
                    )}
                    {profile?.party_drugs && (
                      <Col sm={6} className="mb-3">
                        <small className="text-muted d-block">Party Drugs</small>
                        <span>{getDisplayValue('party_drugs', profile.party_drugs)}</span>
                      </Col>
                    )}
                  </Row>
                </>
              )}

              {/* Financial Information */}
              {(profile?.income || profile?.net_worth) && (
                <>
                  <h5 className="fw-bold mb-3">Financial Information</h5>
                  <Row className="mb-4">
                    {profile?.income && (
                      <Col sm={6} className="mb-3">
                        <small className="text-muted d-block">Income</small>
                        <span>{getDisplayValue('income', profile.income)}</span>
                      </Col>
                    )}
                    {profile?.net_worth && (
                      <Col sm={6} className="mb-3">
                        <small className="text-muted d-block">Net Worth</small>
                        <span>{getDisplayValue('net_worth', profile.net_worth)}</span>
                      </Col>
                    )}
                  </Row>
                </>
              )}

              {profile?.about && (
                <>
                  <h5 className="fw-bold mb-3">About Me</h5>
                  <p>{profile.about}</p>
                </>
              )}

              {profile?.looking_for && (
                <>
                  <h5 className="fw-bold mb-3">What I'm Looking For</h5>
                  <p>{profile.looking_for}</p>
                </>
              )}
            </Card.Body>
          </Card>

          {photos && photos.length > 0 && (
            <Card className="border-0 shadow-sm">
              <Card.Body className="p-4">
                <h4 className="fw-bold mb-4">Photos</h4>
                <div className="photo-gallery">
                  {photos.map((photo, index) => (
                    <div key={index} className="photo-gallery-item">
                      <div
                        className="position-relative photo-container"
                        style={{ height: '200px', overflow: 'hidden' }}
                        onClick={() => handleOpenPhotoModal(index)}
                      >
                        <img
                          src={photo.large_url}
                          alt={`${user.username}'s photo ${index + 1}`}
                          className="photo-image"
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            cursor: 'pointer'
                          }}
                        />
                        {photo.avatar && (
                          <div className="position-absolute top-0 start-0 p-2">
                            <Badge bg="primary">
                              <i className="bi bi-person-badge me-1"></i>
                              Profile Picture
                            </Badge>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </Card.Body>
            </Card>
          )}
        </Col>
      </Row>

      {/* Report Modal */}
      <Modal show={showReportModal} onHide={() => setShowReportModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Report User</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Reason for reporting</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={reportReason}
                onChange={(e) => setReportReason(e.target.value)}
                placeholder="Please provide details about why you're reporting this user"
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowReportModal(false)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleReport}
            disabled={!reportReason.trim() || submitting}
          >
            {submitting ? 'Submitting...' : 'Submit Report'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Block Modal */}
      <Modal show={showBlockModal} onHide={() => setShowBlockModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{isBlocked ? 'Unblock User' : 'Block User'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {isBlocked ? (
            <>
              <p>Are you sure you want to unblock {user?.username}?</p>
              <p>This user will be able to message you and view your profile again.</p>
            </>
          ) : (
            <>
              <p>Are you sure you want to block {user?.username}?</p>
              <p>This user will no longer be able to message you or view your profile.</p>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowBlockModal(false)}>
            Cancel
          </Button>
          <Button
            variant={isBlocked ? "success" : "danger"}
            onClick={handleToggleBlock}
            disabled={submitting}
          >
            {submitting ? 'Processing...' : isBlocked ? 'Unblock User' : 'Block User'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Photo Gallery Modal */}
      <Modal
        show={showPhotoModal}
        onHide={handleClosePhotoModal}
        size="xl"
        centered
        dialogClassName="photo-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {photos && photos.length > 0 && (
              <>
                Photo {selectedPhotoIndex + 1} of {photos.length}
                {photos[selectedPhotoIndex]?.avatar && (
                  <Badge bg="primary" className="ms-2">
                    <i className="bi bi-person-badge me-1"></i>
                    Profile Picture
                  </Badge>
                )}
              </>
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center p-0">
          {photos && photos.length > 0 && (
            <div className="photo-modal-container">
              <img
                src={photos[selectedPhotoIndex]?.large_url || photos[selectedPhotoIndex]?.medium}
                alt={`${user?.username}'s photo ${selectedPhotoIndex + 1}`}
                className="img-fluid photo-modal-image"
                style={{ maxHeight: '80vh', maxWidth: '100%', objectFit: 'contain' }}
              />

              {/* Overlay navigation controls */}
              <div
                className="photo-nav-overlay photo-nav-prev"
                onClick={handlePrevPhoto}
                title="Previous photo"
              >
                <div className="photo-nav-icon">
                  <i className="bi bi-chevron-left"></i>
                </div>
              </div>

              <div
                className="photo-nav-overlay photo-nav-next"
                onClick={handleNextPhoto}
                title="Next photo"
              >
                <div className="photo-nav-icon">
                  <i className="bi bi-chevron-right"></i>
                </div>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-between align-items-center">
          <Button
            variant="outline-secondary"
            onClick={handlePrevPhoto}
            className="btn-circle"
          >
            <i className="bi bi-chevron-left"></i>
          </Button>

          {photos && photos.length > 0 && (
            <div className="text-center">
              <span className="text-muted">
                {selectedPhotoIndex + 1} of {photos.length}
              </span>
            </div>
          )}

          <Button
            variant="outline-secondary"
            onClick={handleNextPhoto}
            className="btn-circle"
          >
            <i className="bi bi-chevron-right"></i>
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Offer Modal */}
      <OfferModal
        show={showOfferModal}
        onHide={() => setShowOfferModal(false)}
        recipient={username}
        onSuccess={handleOfferSuccess}
      />
    </Container>
  );
};

export default UserProfileView;
